import React, { useEffect, useState } from "react";
import { authApi } from "../api";

const initialForm = {
  material_id: "",
  quantity: "",
  rate: "",
  supplier_id: "",
  supplier_name: "",
  supplier_invoice: "",
  location: "",
  notes: "",
};

const BatchHistory = ({ canEdit = false }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(initialForm);

  const loadBatches = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await authApi.get("/inventory/batches");
      setRows(res.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to load batches");
    } finally {
      setLoading(false);
    }
  };

  const loadMaterials = async () => {
    try {
      const res = await authApi.get("/inventory/materials", {
        params: { page: 1, per_page: 500, is_active: true },
      });
      setMaterials(res.data?.materials || []);
    } catch {
      setMaterials([]);
    }
  };

  const loadSuppliers = async () => {
    try {
      const res = await authApi.get("/sales/suppliers", {
        params: { page: 1, limit: 200 },
      });
      setSuppliers(res?.data?.data || []);
    } catch {
      setSuppliers([]);
    }
  };

  useEffect(() => {
    loadBatches();
    if (canEdit) {
      loadMaterials();
      loadSuppliers();
    }
  }, [canEdit]);

  const onFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onSupplierChange = (e) => {
    const supplierId = e.target.value;
    const supplier = suppliers.find((row) => String(row.id) === supplierId);
    setForm((prev) => ({
      ...prev,
      supplier_id: supplierId,
      supplier_name: supplier?.name || "",
    }));
  };

  const openNewBatchModal = () => {
    setForm(initialForm);
    setShowModal(true);
  };

  const submitBatch = async (e) => {
    e.preventDefault();
    if (!canEdit) return;

    setSaving(true);
    setError("");
    try {
      await authApi.post("/inventory/batches", {
        material_id: Number(form.material_id),
        quantity: Number(form.quantity || 0),
        rate: Number(form.rate || 0),
        supplier_name: form.supplier_name || undefined,
        supplier_invoice: form.supplier_invoice || undefined,
        location: form.location || undefined,
        notes: form.notes || undefined,
      });
      setShowModal(false);
      setForm(initialForm);
      await loadBatches();
    } catch (e2) {
      setError(e2?.response?.data?.error || "Failed to add new batch");
    } finally {
      setSaving(false);
    }
  };

  const selectedSupplier = suppliers.find((supplier) => String(supplier.id) === String(form.supplier_id));

  return (
    <div className="h-full overflow-y-auto bg-slate-50 p-4 md:p-6">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <h1 className="text-xl md:text-2xl font-bold text-slate-800">Batch Dispatch History</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={loadBatches}
            className="px-3 py-2 rounded bg-slate-700 text-white text-sm hover:bg-slate-800 min-h-[44px]"
          >
            Refresh
          </button>
          {canEdit && (
            <button
              onClick={openNewBatchModal}
              className="px-3 py-2 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 min-h-[44px]"
            >
              + New Batch Entry
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mt-3 rounded-lg border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {!canEdit && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-700 px-4 py-3 text-sm">
          Read-only access. Contact Inventory Manager/Admin to add new stock batches.
        </div>
      )}

      {loading ? (
        <div className="mt-4 space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-12 bg-slate-200 rounded animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="mt-4 bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 uppercase text-xs">
              <tr>
                <th className="px-4 py-3 text-left">Batch</th>
                <th className="px-4 py-3 text-left">Material</th>
                <th className="px-4 py-3 text-right">Initial Qty</th>
                <th className="px-4 py-3 text-right">Current Qty</th>
                <th className="px-4 py-3 text-right">Rate</th>
                <th className="px-4 py-3 text-left">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="px-4 py-3 font-mono">{r.code}</td>
                  <td className="px-4 py-3">{r.material}</td>
                  <td className="px-4 py-3 text-right">{Number(r.initial_qty || 0).toFixed(3)}</td>
                  <td className={`px-4 py-3 text-right ${Number(r.current_qty || 0) <= 0 ? "text-red-600 font-semibold" : ""}`}>
                    {Number(r.current_qty || 0).toFixed(3)}
                  </td>
                  <td className="px-4 py-3 text-right">{Number(r.rate || 0).toFixed(2)}</td>
                  <td className="px-4 py-3">{r.date}</td>
                </tr>
              ))}
              {!rows.length && (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-slate-400">
                    No batch history found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && canEdit && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-xl max-h-[90vh] bg-white rounded-xl shadow-xl border border-slate-200 modal-responsive overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="text-base font-semibold text-slate-800">New Batch Entry / Stock Update</h3>
              <button onClick={() => setShowModal(false)} className="text-slate-500 hover:text-slate-700 text-xl leading-none">
                ×
              </button>
            </div>
            <form onSubmit={submitBatch} className="p-4 space-y-3 overflow-y-auto">
              <div>
                <label className="text-xs font-semibold text-slate-600">Material</label>
                <select
                  required
                  name="material_id"
                  value={form.material_id}
                  onChange={onFormChange}
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">Select material</option>
                  {materials.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name} ({m.code || "N/A"})
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600">Quantity Received</label>
                  <input
                    required
                    type="number"
                    step="0.001"
                    min="0.001"
                    name="quantity"
                    value={form.quantity}
                    onChange={onFormChange}
                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600">Rate</label>
                  <input
                    required
                    type="number"
                    step="0.01"
                    min="0"
                    name="rate"
                    value={form.rate}
                    onChange={onFormChange}
                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600">Select Supplier</label>
                  <select
                    name="supplier_id"
                    value={form.supplier_id}
                    onChange={onSupplierChange}
                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
                  >
                    <option value="">Select supplier</option>
                    {suppliers.map((supplier) => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}{supplier.city ? ` - ${supplier.city}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600">Supplier Invoice</label>
                  <input
                    type="text"
                    name="supplier_invoice"
                    value={form.supplier_invoice}
                    onChange={onFormChange}
                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600">Supplier Name</label>
                  <input
                    type="text"
                    name="supplier_name"
                    value={form.supplier_name}
                    readOnly
                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-slate-100 text-slate-600"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600">Supplier Contact</label>
                  <input
                    type="text"
                    value={selectedSupplier ? [selectedSupplier.contact_person, selectedSupplier.phone].filter(Boolean).join(" | ") : ""}
                    readOnly
                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-slate-100 text-slate-600"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Supplier Address</label>
                <textarea
                  rows={2}
                  value={selectedSupplier?.full_address || ""}
                  readOnly
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-slate-100 text-slate-600 resize-none"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Storage Location</label>
                <input
                  type="text"
                  name="location"
                  value={form.location}
                  onChange={onFormChange}
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Notes</label>
                <textarea
                  rows={2}
                  name="notes"
                  value={form.notes}
                  onChange={onFormChange}
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div className="pt-2 flex items-center justify-end gap-2 sticky bottom-0 bg-white">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 rounded border border-slate-300 text-slate-700 text-sm min-h-[44px]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-60 min-h-[44px]"
                >
                  {saving ? "Saving..." : "Save Batch"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchHistory;
